import { Card, Checkbox, Input, Spin, Typography } from 'antd';
import FormField from 'components/FormField';
import LocationSelector from 'components/LocationSelector';
import { MediaSelector } from 'components/MediaSelector';
import { DESTINATION_MEDIA, DESTINATION_MEDIA_RATIO } from 'constants/media-sizes.constants';
import { PurchaseStockPhotoWarningModal } from 'components/StockPhotoWarning';
import React, { useCallback } from 'react';
import type { DestinationTravelThemeSchema } from 'typings/destinations/DestinationTravelThemeSchema';
import styles from './DestinationEditorBasicInfo.module.css';
import { DestinationBasicInfoCommonFields } from './DestinationBasicInfoCommonFields';
import { DestinationCountrySelector } from './DestinationCountrySelector';
import { DestinationTravelThemesCard } from './DestinationTravelThemesCard';
import { useDestinationBasicInfoForm } from './use-destination-basic-info-form';
import { useDestinationEditor } from './DestinationEditorContext';
import { DestinationEditorTabActions } from './DestinationEditorTabActions';

const { Text } = Typography;

type PlaceDestinationBasicInfoProps = Readonly<{
    onClickDiscard: (hasUnsavedChanges: boolean) => void;
}>;

export function PlaceDestinationBasicInfo({ onClickDiscard }: PlaceDestinationBasicInfoProps) {
    const {
        destination,
        location,
        isCountry,
        fieldErrors,
        taglineContent,
        setTaglineContent,
        descriptionContent,
        setDescriptionContent,
        isDirty,
        isSaving,
        isContentCurated,
        isTogglingContentCurated,
        isEditing,
        hasContent,
        imageToPurchase,
        isAddingStockPhotoWarningShown,
        setIsAddingStockPhotoWarningShown,
        handleUpdateCoverImage,
        handleSave,
        handleAddPurchasedPhoto,
        handleUpdateField,
        handleUpdateTaglineOrDescription,
        handleToggleContentCurated,
        handleUpdateEditor,
        handleUpdateCountryDestination,
        handleLocationChange,
    } = useDestinationBasicInfoForm();
    const { updateDestination } = useDestinationEditor();

    const handleTravelThemesUpdate = useCallback(
        (themes: DestinationTravelThemeSchema[]) => {
            updateDestination((prev) => ({
                ...prev,
                travel_themes: themes,
            }));
        },
        [updateDestination]
    );
    return (
        <>
            <div className={styles.root}>
                <div className={styles.content}>
                    <Card
                        title={<Text strong>Basic Info</Text>}
                        extra={
                            <Checkbox
                                checked={isContentCurated}
                                onChange={handleToggleContentCurated}
                                disabled={!isEditing || isTogglingContentCurated || !hasContent}>
                                Curated content {isTogglingContentCurated ? <Spin size='small' /> : null}
                            </Checkbox>
                        }>
                        <FormField label='Location' error={fieldErrors.location}>
                            <LocationSelector
                                value={location}
                                placeholder='Search for a location'
                                hasError={!!fieldErrors.location}
                                onChange={handleLocationChange}
                                searchMode={isCountry ? 'countries' : 'places'}
                                getSelectedName={(selectedLocation) => selectedLocation.name}
                            />
                        </FormField>
                        <FormField label='Destination name' error={fieldErrors.name}>
                            <Input
                                value={destination.name ?? ''}
                                onChange={(e) => handleUpdateField('name', e.target.value)}
                                size='large'
                                status={fieldErrors.name ? 'error' : undefined}
                            />
                        </FormField>
                        <FormField label='Country'>
                            <DestinationCountrySelector value={destination} onChange={handleUpdateCountryDestination} />
                        </FormField>
                        <DestinationBasicInfoCommonFields
                            fieldErrors={fieldErrors}
                            taglineContent={taglineContent}
                            setTaglineContent={setTaglineContent}
                            descriptionContent={descriptionContent}
                            setDescriptionContent={setDescriptionContent}
                            destination={destination}
                            handleUpdateTaglineOrDescription={handleUpdateTaglineOrDescription}
                            handleUpdateField={handleUpdateField}
                            handleUpdateEditor={handleUpdateEditor}
                            showPopularity
                        />
                    </Card>
                </div>
                <Card title='Cover photo' className={styles.coverCard}>
                    <MediaSelector
                        initialMediaSource='UNSPLASH'
                        showCropperOnEdit={false}
                        disallowedSources={['FAVORITES']}
                        location={location}
                        includeVideos={false}
                        onSelect={handleUpdateCoverImage}
                        onDelete={() => handleUpdateCoverImage(null)}
                        media={destination.cover_image ?? null}
                        ratio={DESTINATION_MEDIA_RATIO}
                    />
                </Card>
                {isEditing && destination.id ? (
                    <div className={styles.themesCard}>
                        <DestinationTravelThemesCard
                            destinationId={destination.id}
                            travelThemes={destination.travel_themes ?? []}
                            onUpdate={handleTravelThemesUpdate}
                        />
                    </div>
                ) : null}
            </div>
            <DestinationEditorTabActions
                tabKey='basicInfo'
                saveLabel='Save Basic Info'
                isSaving={isSaving}
                isSaveDisabled={!isDirty}
                onCancel={() => onClickDiscard(isDirty)}
                onSave={handleSave}
            />
            {isAddingStockPhotoWarningShown && imageToPurchase ? (
                <PurchaseStockPhotoWarningModal
                    photosToPurchase={[imageToPurchase]}
                    onAdd={handleAddPurchasedPhoto}
                    onCancel={() => setIsAddingStockPhotoWarningShown(false)}
                    sizesToUpload={DESTINATION_MEDIA}
                />
            ) : null}
        </>
    );
}
